import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import {
  arrayOf,
  customType,
  namedFragment,
  params,
  shape,
  string as stringType,
  typename,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ShopByCategoryRenderer } from './ShopByCategoryRenderer';
import { formatCategoryData } from '../utils/formatCategoryData';

const { string, bool } = PropTypes;

const ShopByCategory = ({
  componentId = '',
  componentClass = '',
  enableB2CGapAndPadding = false,
  slotContentId: slotName = '',
  mediaCollectionIndex = 0,
}) => {
  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    skip: !!slotName || !componentId || !componentClass,
  });

  const {
    data: slotData,
    loading: slotLoading,
    error: slotError,
  } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: { id: slotName },
    skip: !slotName,
  });

  useEffect(() => {
    if (window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('shop-by-category.ready');
    }
  }, []);

  const rawCategoryList = data?.component?.shopByCategoryNavigationItems
   || slotData?.slot?.mediaCollections[mediaCollectionIndex]?.media;
  const categoryList = formatCategoryData(rawCategoryList, !!slotData?.slot?.mediaCollections);

  if (error || slotError || (!categoryList?.length && !loading && !slotLoading)) {
    return null;
  }
  return (
    <ImpressionProvider
      data={{
        id: componentId || slotData?.slot?.mediaCollections[mediaCollectionIndex]?.entryId || '',
        name: 'ShopByCategory',
        component: 'ShopByCategory',
        type: 'content',
        position: '',
      }}
    >
      <ShopByCategoryRenderer
        categoryList={categoryList}
        data={data || slotData}
        loading={loading || slotLoading}
        enableB2CGapAndPadding={enableB2CGapAndPadding}
      />
    </ImpressionProvider>
  );
};

ShopByCategory.propTypes = {
  componentId: string,
  componentClass: string,
  enableB2CGapAndPadding: bool,
  slotContentId: string,
  mediaCollectionIndex: PropTypes.number,
};

ShopByCategory.displayName = 'ShopByCategory';

// Do not touch
const DamMediaFragment = namedFragment({
  inline: false,
  fragmentType: 'DamMedia',
  fragmentAlias: 'DamMediaV1'
}).shape({
  damContentSelector: shape({
    assetData: arrayOf(
      shape({
        selectedImageUrl: stringType()
      })
    )
  }),
  damDownloadedContent: shape({
    url: stringType()
  }),
  __typename: typename('DamMedia')
});

ShopByCategory.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['ShopByCategory'])
      .isRequired(),
  }).shape({
    ShopByCategory: namedFragment({ inline: true, fragmentType: 'ShopByCategory' }).shape({
      id: stringType(),
      componentName: stringType(),
      shopByCategoryNavigationItems: arrayOf(
        shape({
          id: stringType(),
          title: stringType(),
          link: stringType(),
          icon: shape({
            url: stringType(),
          }),
          stencilIcon: stringType(),
          previewImage: DamMediaFragment,
        }),
      ),
      __typename: typename('ShopByCategory'),
    }),
  }),
  slot: params({
    id: stringType().isRequired(),
  }).shape({
    id: stringType(),
    entryId: stringType(),
    mediaCollections: arrayOf(
      shape({
        entryId: stringType(),
        media: arrayOf(
          shape({
            entryId: stringType(),
            title: stringType(),
            stencilIcon: stringType(),
            sourceDesktop: arrayOf(
              shape({
                entryId: stringType(),
                uri: stringType(),
                hyperlink: stringType(),
              })
            ),
            sourceMobile: arrayOf(
              shape({
                entryId: stringType(),
                uri: stringType(),
                hyperlink: stringType(),
              })
            ),
          })
        ),
      })
    ),
    __typename: typename('newSlot'),
  }),

};

export { ShopByCategory };
